import {
  OfflineTransactionPaymentMethod,
  PayBillType,
  TransactionPaymentMethod,
  MaintenanceRequestStatus,
  TransactionStatus,
  CannotSendTextMessagesReason,
  TransactionFailure,
  LeaseTemplateType,
  FlyerTemplate,
  LeaseMoveOutRequestStatus,
  BankAccountConnectionStatus,
  ChargeFrequency,
  ConditionOperator,
  LegalEntityType,
  WorkOrderStatus,
} from '~/swagger/Api';
import { Dict } from '~/utils/dict';
import type { JSX } from 'solid-js';
import type { ConfirmationModalProps } from '~/components/modals/ConfirmationModal';

export const genderTypes = [
  { label: 'male', value: 'male' },
  { label: 'female', value: 'female' },
];

export const emptyPlaceholder = '--';

export const priorityTypes = ['urgent', 'high', 'medium', 'low', 'none'].map((item) => ({ label: item, value: item }));

// TODO 后端API 会减少状态类型
export const maintenanceStatusTypes: Array<{
  label: string;
  value: MaintenanceRequestStatus;
}> = [
  {
    label: 'Pending',
    value: MaintenanceRequestStatus.Pending,
  },
  {
    label: 'In progress',
    value: MaintenanceRequestStatus.InProgress,
  },
  {
    label: 'Closed',
    value: MaintenanceRequestStatus.Closed,
  },
  {
    label: 'AI processing',
    value: MaintenanceRequestStatus.AiProcessing,
  },
  {
    label: 'Waiting for work order',
    value: MaintenanceRequestStatus.WaitingForWorkOrder,
  },
];

export const getMaintenanceStatusOptions = (status: MaintenanceRequestStatus) => {
  if (status !== 'aiProcessing') {
    return maintenanceStatusTypes.filter((item) => item.value !== 'aiProcessing');
  }
  return maintenanceStatusTypes;
};

export const permissionToEnterTypes = (t: (str: string) => string) => [
  {
    label: t('Yes'),
    value: true,
  },
  {
    label: t('No'),
    value: false,
  },
];

export const formConfirmationContent = (
  t: (str: string) => string
): Pick<ConfirmationModalProps, 'title' | 'message' | 'cancelText' | 'confirmText'> => ({
  title: t('Are you sure you want to cancel?'),
  message: t('If you cancel the addition, the information already filled in cannot be saved'),
  confirmText: t('Yes'),
  cancelText: t('Go back'),
});

export enum currentUpdateType {
  work = 'work',
  rental = 'rental',
  document = 'document',
  rentalInfo = 'rentalInfo',
  receivePayment = 'receivePayment',
}

export enum rentalApplicationPaymentStatus {
  processing = 'processing',
  readyToConnect = 'transunion-connection',
  confirmIdentity = 'manual-verification',
  submitted = 'application-submitted',
  questionnaire = 'questionnaire',
}
export const workOrderStatusTypes: Array<{
  label: string;
  value: WorkOrderStatus;
}> = [
  {
    label: 'Pending',
    value: WorkOrderStatus.Pending,
  },
  {
    label: 'In progress',
    value: WorkOrderStatus.InProgress,
  },
  {
    label: 'Closed',
    value: WorkOrderStatus.Closed,
  },
];

export const transactionStatusOptions = (t: (key: string) => string) => [
  { value: TransactionStatus.Pending, label: t('Pending') },
  { value: TransactionStatus.Failed, label: t('Failed') },
  { value: TransactionStatus.Success, label: t('Success') },
  { value: TransactionStatus.Cancelled, label: t('Cancelled') },
  { value: TransactionStatus.Returned, label: t('Returned') },
  { value: TransactionStatus.Refunded, label: t('Refunded') },
];

export const offlineTransactionPaymentMethodsDict = new Dict([
  {
    label: 'Check',
    value: OfflineTransactionPaymentMethod.Check,
  },
  {
    label: "Cashier's check",
    value: OfflineTransactionPaymentMethod.CashiersCheck,
  },
  {
    label: 'Money order',
    value: OfflineTransactionPaymentMethod.MoneyOrder,
  },
  {
    label: 'Cash',
    value: OfflineTransactionPaymentMethod.Cash,
  },
  {
    label: 'Other',
    value: OfflineTransactionPaymentMethod.Other,
  },
]);

export const enumToOptions = (enumObj: Record<string, string>, renderLabel?: (value: any) => JSX.Element) => {
  return Object.entries(enumObj).map(([key, value]) => ({ label: renderLabel ? renderLabel(value) : key, value }));
};

export enum BackendEntitiesEnum {
  Portfolios = 'portfolios',
  Properties = 'properties',
  Units = 'units',
  Leases = 'leases',
  Tenants = 'tenants',
  Vendors = 'vendors',
  MaintenanceRequests = 'maintenance-requests',
  AssociationsViolations = 'associations/violations',
}

export const isDebug = () => import.meta.env.DEV || ['test', 'demo'].some((host) => location.hostname.includes(host));
export const isDemo = () => ['demo'].some((host) => location.hostname.includes(host));

export const paymentTypeOptions = (t: (str: string) => string) => [
  { label: t('Manual'), value: PayBillType.Manual },
  { label: t('Print check'), value: PayBillType.PrintCheck },
  { label: t('ACH'), value: PayBillType.Ach },
];

export const transactionPaymentMethodsDict = new Dict([
  { label: 'ACH', value: TransactionPaymentMethod.Ach },
  { label: 'Credit', value: TransactionPaymentMethod.Credit },
  { label: 'Debit card', value: TransactionPaymentMethod.DebitCard },
  { label: 'Wallet', value: TransactionPaymentMethod.Wallet },
  { label: 'Credit card', value: TransactionPaymentMethod.CreditCard },
  { label: 'Check', value: TransactionPaymentMethod.Check },
  { label: "Cashier's check", value: TransactionPaymentMethod.CashiersCheck },
  { label: 'Money order', value: TransactionPaymentMethod.MoneyOrder },
  { label: 'Cash', value: TransactionPaymentMethod.Cash },
  { label: 'Other', value: TransactionPaymentMethod.Other },
]);

export const leaseTemplateTypeDict = new Dict([
  { value: LeaseTemplateType.Lease, label: 'Lease' },
  { value: LeaseTemplateType.LeaseRenewal, label: 'Lease renewal' },
  { value: LeaseTemplateType.LateRentNotice, label: 'Late rent notice' },
  { value: LeaseTemplateType.Eviction, label: 'Eviction' },
  { value: LeaseTemplateType.SignatureOnly, label: 'Signature only' },
  { value: LeaseTemplateType.Information, label: 'Information' },
]);

export const imageType = ['.jpg', '.jpeg', '.png', '.webp'];

export const themeColors = [
  {
    label: 'blue',
    value: '#2F49D0',
  },
  {
    label: 'green',
    value: '#06603A',
  },
  {
    label: 'orange',
    value: '#B64F16',
  },
  {
    label: 'purple',
    value: '#4D0061',
  },
  {
    label: 'black',
    value: '#2D2D2D',
  },
];

export const MAX_INPUT_AMOUNT = 2147483647;
export const MAX_INPUT_AMOUNT_2 = 1000000000000;

export const NO_LIMIT = 'noLimit';

export const LAST_DELETED_LEASE_ID = 'lastDeletedLeaseId';

export const REPORT_LOAD_FAILED_MARK = 'reportLoadFailedMark';

export const NEW_RENTAL_APPLICATION_REVIEW_STEP = Infinity;

export enum OnboardingStatus {
  GET_VERIFIED_CHECKLIST = 'get_verified_checklist',
  IMPORTING = 'importing',
  PAID = 'paid',

  USER_HIDE_ONBOARDING = 'user_hide_onboarding',
}

export enum OnboardingChecklistModalType {
  PhoneNumber = 'phoneNumber',
  PaymentAccounts = 'paymentAccounts',
  BusinessInfo = 'businessInfo',
  BankAccounts = 'bankAccounts',
  AddBankAccounts = 'addBankAccounts',
  StripeConnected = 'stripeConnected',
  PlaidConnected = 'plaidConnected',
  ReceivePayment = 'receivePayment',
  WelcomeVideo = 'welcomeVideo',
}

export const CannotSendTextMessagesReasonMap: Record<CannotSendTextMessagesReason, string> = {
  [CannotSendTextMessagesReason.HasNoPhoneNumber]: 'Has no phone number',
  [CannotSendTextMessagesReason.TcrCampaignNotApproved]: 'TCR campaign not approved',
};
export const transactionFailureDict = new Dict([
  { label: 'Insufficient funds', value: TransactionFailure.NoSufficientFunds },
  { label: 'Unknown', value: TransactionFailure.Unknown },
  { label: 'Transfer limit reached', value: TransactionFailure.TransferLimitReached },
  { label: 'Card lost', value: TransactionFailure.CardLost },
  { label: 'Stolen card', value: TransactionFailure.StolenCard },
  { label: 'Expired card', value: TransactionFailure.ExpiredCard },
  { label: 'Company account setup error', value: TransactionFailure.CompanyAccountSetupError },
  { label: 'Payment processor declined', value: TransactionFailure.PaymentProcessorDeclined },
  { label: 'Payment account setup error', value: TransactionFailure.PaymentAccountSetupError },
]);

export const flyers = new Dict([
  {
    label: "We're moving to MagicDoor!",
    value: FlyerTemplate.QrCode,
  },
  {
    label: "Exciting news! We're using MagicDoor",
    value: FlyerTemplate.TenantNotice,
  },
  {
    label: 'Welcome to your new home!',
    value: FlyerTemplate.Welcome,
  },
]);

export const IS_USER_LOGIN_FROM_INTERNAL_KEY = 'isUserLoginFromInternal';

export const LeaseMoveOutRequestStatusDict = new Dict([
  { label: 'Pending', value: LeaseMoveOutRequestStatus.Requested, meta: { color: 'text-warning' } },
  { label: 'Approved', value: LeaseMoveOutRequestStatus.Approved, meta: { color: 'text-success' } },
  { label: 'Rejected', value: LeaseMoveOutRequestStatus.Rejected, meta: { color: 'text-warning' } },
  { label: 'Retracted', value: LeaseMoveOutRequestStatus.Retracted, meta: { color: 'text-text-level03' } },
]);
export const bankAccountConnectionStatusDict = new Dict(
  [
    {
      label: 'Pending',
      value: BankAccountConnectionStatus.Pending,
      meta: {
        bgColor: 'bg-[#FFF4D7]',
        textColor: 'text-[#F45A29]',
        circleColor: 'bg-[#F45A29]',
      },
    },
    {
      label: 'Connected',
      value: BankAccountConnectionStatus.Approved,
      meta: {
        bgColor: 'bg-[#C8FFEE]',
        textColor: 'text-[#009D7C]',
        circleColor: 'bg-[#009D7C]',
      },
    },
    {
      label: 'Denied',
      value: BankAccountConnectionStatus.Denied,
      meta: {
        bgColor: 'bg-[#FFF1F6]',
        textColor: 'text-[#DD3668]',
        circleColor: 'bg-[#DD3668]',
      },
    },
    {
      label: 'Incomplete',
      value: BankAccountConnectionStatus.Incomplete,
      meta: {
        bgColor: 'bg-[#E2E9F2]',
        textColor: 'text-[#6C80AF]',
        circleColor: 'bg-[#6C80AF]',
      },
    },
  ],
  {
    defaultMeta: {
      bgColor: 'bg-[#E2E9F2]',
      textColor: 'text-[#6C80AF]',
      circleColor: 'bg-[#6C80AF]',
    },
    labelPlaceholder: 'Not Connected',
  }
);

export const chargeFrequencyOptionsDict = new Dict([
  { label: '1 payment monthly', value: ChargeFrequency.Monthly },
  { label: '2 payments monthly', value: ChargeFrequency.Monthly2XSplit },
  { label: 'Weekly', value: ChargeFrequency.Weekly },
  { label: 'Daily', value: ChargeFrequency.Daily },
  { label: 'Yearly', value: ChargeFrequency.Yearly },
]);

export const conditionOperatorsDict = new Dict<string, string>([
  { label: '=', value: ConditionOperator.Equal },
  { label: '!=', value: ConditionOperator.NotEqual },
  { label: '>', value: ConditionOperator.GreaterThan },
  { label: '<', value: ConditionOperator.LessThan },
  { label: '>=', value: ConditionOperator.GreaterThanOrEqual },
  { label: '<=', value: ConditionOperator.LessThanOrEqual },
]);
export enum LoginStep {
  MagicLinkInputEmail = 'MagicLinkInputEmail',
  OtpInputPhone = 'OtpInputPhone',
  OtpInputVerifyCode = 'OtpInputVerifyCode',
  Password = 'Password',
}

export enum LoginType {
  MagicLink = 'MagicLink',
  Otp = 'Otp',
  Password = 'Password',
}

export const lateFeeFormulaVariablesDict = new Dict([
  { label: 'Due', value: 'due', meta: { description: 'The amount currently due' } },
  { label: 'Total', value: 'total', meta: { description: 'The total amount of the bill' } },
  { label: 'Late Bills', value: 'lateBills', meta: { description: 'The number of previous late bills' } },
  { label: 'Late Days', value: 'lateDays', meta: { description: 'The number of late days' } },
  { label: 'Grace Period', value: 'gracePeriod', meta: { description: 'The grace period of the bill' } },
  { label: 'Grace Balance', value: 'graceBalance', meta: { description: 'The grace balance of the bill' } },
]);

export const lateFeeFormulaTemplates = new Dict([
  {
    label: 'Fixed on nth day, fixed daily from n+1 on',
    value: 'if(lateDays < n, 0, if(lateDays = n, fixedAmount1, fixedAmount2))',
  },
  {
    label: 'Percentage on nth, fixed daily from n+1 on',
    value: 'if(lateDays < n, 0, if(lateDays = n, due * percentage, fixedAmount))',
  },
]);

export const legalEntityTypeOptionsDict = new Dict([
  { label: 'Sole Proprietorship', value: LegalEntityType.SoleProprietorship },
  { label: 'LLC', value: LegalEntityType.Llc },
  { label: 'Private Company', value: LegalEntityType.PrivateCompany },
  { label: 'Public Company Unlisted', value: LegalEntityType.PublicCompanyUnlisted },
  { label: 'Public Company Listed', value: LegalEntityType.PublicCompanyListed },
  { label: 'Charitable Organization Not For Profit', value: LegalEntityType.CharitableOrganizationNotForProfit },
  { label: 'Partnership', value: LegalEntityType.Partnership },
]);

export const chargeFrequencys = (t: (str: string) => string) => [
  { label: t('Yearly'), value: 'yearly' },
  { label: t('Monthly'), value: 'monthly' },
  { label: t('Twice a month'), value: 'monthly2XSplit' },
  { label: t('Weekly'), value: 'weekly' },
  { label: t('Daily'), value: 'daily' },
];

export enum tenantTabs {
  newApplicant = 'new-applicant',
  existingTenant = 'existing-tenant',
}
